import axios from 'axios';
import React, { useContext, createContext, useState } from 'react';
import { API_URL } from '../utils/constants';
import StorageService from '../utils/storage';

export interface IAuthContext {
  prefix?: string;
  isAuthenticated: boolean;
  isAdmin: boolean;
  signin: (isAdmin: boolean, username: String, password: string) => void;
  signout: (callback: () => void) => void;
  refreshUserData: () => void;
}

const AuthContext = createContext<IAuthContext>({
  prefix: '',
  isAuthenticated: false,
  isAdmin: false,
  signin: () => {},
  signout: () => {},
  refreshUserData: () => {},
});

export function ProvideAuth({
  prefix = '',
  children,
}: {
  prefix?: string;
  children: React.ReactNode;
}) {
  const auth = useProvideAuth(prefix);
  return <AuthContext.Provider value={{ prefix, ...auth }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

function useProvideAuth(prefix: string) {
  const [isAuthenticated, setIsAuthenticated] = useState(
    StorageService.get('userData', prefix)?._id !== undefined
  );
  const [isAdmin, setIsAdmin] = useState(StorageService.get('userData', prefix)?.level === '10');

  const signin = async (isAdmin: boolean, username: String, password: string) => {
    try {
      const result = await axios.post(`${API_URL}/${isAdmin ? 'admin/' : ''}api/login`, {
        username,
        password,
      });
      StorageService.set('userData', result.data.user, prefix);
      StorageService.set('isAuthenticated', true, prefix);
      StorageService.set('accessToken', result.data.accessToken, prefix);
      StorageService.set('refreshToken', result.data.refreshToken, prefix);
      setIsAuthenticated(true);
      setIsAdmin(result.data.user.level === '10');
      return {
        success: true,
        message: 'Inicio de sesión completado, bienvenido',
        data: result.data,
      };
    } catch (error: any) {
      console.log(error);
      StorageService.set('userData', null, prefix);
      setIsAuthenticated(false);
      setIsAdmin(false);
      return {
        success: false,
        message: error.response != null ? error.response.data.err : 'Ocurrió un error',
      };
    }
  };

  const refreshUserData = async () => {
    try {
      const result = await axios.post(`${API_URL}/user`, {
        user: StorageService.get('userData', prefix)._id,
      });
      StorageService.set('userData', result.data.user, prefix);
      return {
        success: true,
        message: 'Usuario verificado, bienvenido',
        data: result.data,
      };
    } catch (error: any) {
      console.log(error);
      return {
        success: false,
        message: error.response != null ? error.response.data.err : 'Ocurrió un error',
      };
    }
  };

  const signout = (callback: () => void) => {
    StorageService.set('userData', null, prefix);
    StorageService.set('isAuthenticated', false, prefix);
    StorageService.set('accessToken', null, prefix);
    StorageService.set('refreshToken', null, prefix);
    setIsAuthenticated(false);
    setIsAdmin(false);
    callback();
  };

  return {
    isAuthenticated,
    isAdmin,
    signin,
    signout,
    refreshUserData,
  };
}
